import React, { useContext, useState, useMemo, useEffect } from 'react'
import constants from "../../constants";
import AlertContext from "../../context/AlertContext";
import useShareScopeHook from '../../Hooks/useShareScopeHook';
import Modal from './Modal';
import ModalBody from './ModalBody';
import Button from "../Button";
import Table from './Table';
import Search from './Search';
import { useDebouncedValue } from "../../Hooks/useDebounceValue";

const Icon = ({ name = "bookOpen" }) => {
    const icons = {
        closeIcon: (
            <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M15 5L5 15M5 5L15 15"
                    stroke="#667085"
                    strokeWidth="1.66667"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        ),
        bookOpen: (
            <svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 56 56" fill="none">
                <rect x="4" y="4" width="48" height="48" rx="24" fill="#F8E0DB" />
                <rect x="4" y="4" width="48" height="48" rx="24" stroke="#FCEBE8" stroke-width="8" />
                <path d="M28 23C28 21.9391 27.5786 20.9217 26.8284 20.1716C26.0783 19.4214 25.0609 19 24 19H18V34H25C25.7956 34 26.5587 34.3161 27.1213 34.8787C27.6839 35.4413 28 36.2044 28 37M28 23V37M28 23C28 21.9391 28.4214 20.9217 29.1716 20.1716C29.9217 19.4214 30.9391 19 32 19H38V34H31C30.2044 34 29.4413 34.3161 28.8787 34.8787C28.3161 35.4413 28 36.2044 28 37" stroke="#F55533" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
        ),
    };
    return icons[name] || icons.bookOpen;
};

const SpecificSharedTitle = ({ topicName }) => {
    return <div className="specificSharedTitle">
        <span><Icon name="bookOpen" /></span>
        <div className="specificSharedTitleDiv">
            <div className="specificShareTitleHeader">Sharing access for "{topicName}"</div>
            <div className="specificSharedTitlesubheader">The following colleges have access to this topic</div>
        </div>
    </div>
}

const TableHeaderWithSearch = React.memo(
    ({ title, value, onChange }) => {
        return (
            <div className="table-header-with-search">
                <span className="college-name">{title}</span>
                <Search
                    placeholder="Search"
                    value={value}
                    onChange={onChange}
                    iconPosition="end"
                    className="table-header-search"
                />
            </div>
        );
    }
);

const SharedScopeSpecificSelectionTable = ({ selectedRow, onUpdateSuccess, openModal, setOpenModal, from }) => {
    // console.log("shareddddtable", selectedRow);
    const { setShowNotify, universityList } = useContext(AlertContext);
    const { updateShareScope } = useShareScopeHook();

    const [selectedIds, setSelectedIds] = useState([]);
    const [data, setData] = useState(universityList ?? []);
    const [searchText, setSearchText] = useState("");
    const debouncedSearchTerm = useDebouncedValue(searchText, 1000);

    const handleShare = async () => {
        setShowNotify({show: false}); // reset notify
        const payload = {
            shareScope: constants.SHARED_SCOPE.SPECIFIC,
            sharedCompanyIds: selectedIds // IDs only (backend)
        };

         if (from === "topic") {
            payload.topicId = selectedRow.tid;
        } else {
            payload.lpId = selectedRow.id;
        }

        // build id → name map
        const companyMap = new Map(
            data.map((company) => [company.company_id, company.company_name])
        );

        // selected ids → full objects
        const updatedSharedCompanies = selectedIds.map((id) => ({
            company_id: id,
            company_name: companyMap.get(id)
        }));

        // console.log("updatedSharedCompanies", updatedSharedCompanies);

        await updateShareScope(
            payload,
            onUpdateSuccess,
            from,
            updatedSharedCompanies
        );

    };


    const handleDisable = () => {
        // CASE 1: nothing selected
        if (!selectedIds?.length) return true;

        const existingIds = selectedRow.sharedCompanies?.map(c => c.company_id) || [];

        // CASE 2: length mismatch → definitely changed
        if (selectedIds.length !== existingIds.length) return false;

        // CASE 3: same length → check content
        const selectedSet = new Set(selectedIds);

        const isSame = existingIds.every(id => selectedSet.has(id));

        return isSame; // true = disable, false = enable
    };


    const handleSearchChange = React.useCallback((e) => {
        setSearchText(e.target.value);
    }, []);

    const columns = useMemo(() => [
        {
            key: "company_name",
            title: "University Name",
            renderHeader: () => (
                <TableHeaderWithSearch
                    title="University Name"
                    value={searchText}
                    onChange={handleSearchChange}
                />
            ),
        },
    ], [handleSearchChange, searchText]);

    useEffect(() => {
        if (openModal && selectedRow.sharedCompanies?.length) {
            setSelectedIds(selectedRow.sharedCompanies.map(c => c.company_id));
        }
        if (!openModal) {
            setSearchText("");
        }
    }, [openModal, selectedRow.sharedCompanies]);

    useEffect(() => {
        if (universityList?.length) {
            setData(universityList);
        }
    }, [universityList]);


    const filteredData = useMemo(() => {
        if (!debouncedSearchTerm) return data;

        return data.filter(item =>
            item.company_name
                ?.toLowerCase()
                .includes(debouncedSearchTerm.toLowerCase())
        );
    }, [data, debouncedSearchTerm]);


    return (
        <Modal
            isOpen={openModal}
            title={<SpecificSharedTitle topicName={selectedRow.topic || selectedRow.name} />}
            showCloseIcon
            onClose={() => setOpenModal(!openModal)}
            className="specificSharedScopeContainer"
            closeIcon={<Icon name="closeIcon" />}
        >
            <ModalBody
                content={
                    <div className="admin-specific-university-container">
                        <div className="admin-specific-university-count">({selectedIds.length} Selected)</div>
                        <Table
                            className="admin-specific-university-selection"
                            rowKey="company_id"
                            columns={columns}
                            data={filteredData}
                            selectable
                            selectedRows={selectedIds}
                            onRowSelect={setSelectedIds}
                            fixedHeader
                        />
                    </div>
                }
                footer={
                    <>
                        <Button
                            size="lg"
                            hierarchy={{ type: "secondaryGrey", buttonText: "Cancel" }}
                            onClick={() => setOpenModal(!openModal)}
                            className='sharedScopeButton'
                        />
                        <Button
                            size="lg"
                            disable={handleDisable()}
                            hierarchy={{ type: "primary", buttonText: "Share" }}
                            onClick={(event) => {
                                event.stopPropagation();
                                setOpenModal(false); // Close openModal immediately

                                setShowNotify({
                                    show: true,
                                    titleSvg: "warning",
                                    primaryButton: "Confirm",
                                    title: "Are you sure you want to modify sharing?",
                                    secondaryButton: "Cancel",
                                    msg: `The ${from} '${selectedRow.topic || selectedRow.name}' will be shared only with selected colleges.`,
                                    // job: true,
                                    primaryButtonFunction: () => handleShare(),
                                });
                            }}
                            className='sharedScopeButton'
                        />
                    </>
                }
            />
        </Modal>
    )
}

export default SharedScopeSpecificSelectionTable;
